import {
  Divider,
  IconDocument,
  Text,
  TextLink,
  TableRow,
  TableCell,
} from 'braid-design-system';
import type { GraphQLObjectType } from 'graphql';
import { InlineCode } from 'scoobie';

import { SchemaSection } from '#src/components/SchemaSection/SchemaSection.tsx';
import { hrefForWebhookSchemaDefinition } from '#src/utils/href.ts';
import { type ObjectType, isObjectType } from '#src/utils/jsonSchema.ts';
import { sortGraphqlEntities } from '#src/utils/sort.ts';

import { FieldDetails } from '../components/FieldDetails.tsx';
import { SchemaMarkdown } from '../components/SchemaMarkdown.tsx';
import { TypeSeenIn } from '../components/TypeSeenIn.tsx';
import { TypeSnippet } from '../components/TypeSnippet.tsx';
import { webhookSchema } from '../webhookSchema.ts';
import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import { useLocation } from 'react-router';

const WebhookPayloadSection = ({
  name,
  definition,
}: {
  name: string;
  definition: ObjectType;
}) => (
  <SchemaSection heading="Webhook event payload">
    <TableRow>
      <TableCell width="content">
        <Text>
          <InlineCode weight="weak">
            <TextLink href={hrefForWebhookSchemaDefinition(name)}>
              {name}
            </TextLink>
          </InlineCode>
        </Text>
      </TableCell>

      <TableCell wrap>
        {typeof definition.description === 'string' && (
          <SchemaMarkdown type={`webhook.${name}`} />
        )}
      </TableCell>
    </TableRow>
  </SchemaSection>
);

export const ObjectSchemaPage = ({
  objectType,
  fieldName,
}: {
  objectType: GraphQLObjectType;
  fieldName?: string;
}) => {
  const interfaces = objectType.getInterfaces();

  const isEventImplementation =
    interfaces.length === 1 && interfaces[0].name === 'Event';

  const webhookDefinition = webhookSchema.definitions[objectType.name];

  return (
    <BasePage
      title={{
        children: <InlineCode weight="weak">{objectType.name}</InlineCode>,
        htmlTitle: objectType.name,
        stringTitle: objectType.name,
        subHeading: (
          <Text
            icon={<IconDocument />}
            size="small"
            weight="medium"
            tone="secondary"
          >
            Object
          </Text>
        ),
      }}
      breadcrumbs={[
        { href: '/schema', title: 'Schema' },
        { href: useLocation().pathname, title: objectType.name },
      ]}
    >
      <SchemaMarkdown type={objectType.name} />

      {interfaces.length > 0 && (
        <>
          <Divider />
          <SchemaSection heading="Implements">
            {interfaces.map((interfaceType) => (
              <TypeSnippet key={interfaceType.name} type={interfaceType} />
            ))}
          </SchemaSection>
        </>
      )}

      <Divider />

      <SchemaSection heading="Fields" wrapper="usage-table">
        {Object.entries(objectType.getFields())
          .sort(sortGraphqlEntities)
          .map(([fieldNameKey, field]) => (
            <FieldDetails
              key={fieldNameKey}
              objectName={objectType.name}
              highlight={fieldName === fieldNameKey}
              field={field}
              deprecated={typeof field.deprecationReason === 'string'}
            />
          ))}
      </SchemaSection>

      <Divider />

      {isEventImplementation &&
      webhookDefinition &&
      isObjectType(webhookDefinition) ? (
        <WebhookPayloadSection
          name={objectType.name}
          definition={webhookDefinition}
        />
      ) : (
        <TypeSeenIn name={objectType.name} />
      )}
    </BasePage>
  );
};
